<template>
    <pendo-card
        data-cy="import-feedback-preview"
        class="import-feedback-preview">
        <h3>Preview</h3>
        <pendo-table
            :data="tableData"
            :columns="tableHeaders"
            max-height="1000">
            <template #headerLeft>
                <span
                    ref="refTableTitle"
                    v-pendo-tooltip="{ content: tableTitleTooltip }"
                    class="import-feedback-preview__table-title">{{ tableTitle }}</span>
                <div class="pendo-table__active-count">
                    <span>({{ rows }} of {{ totalRows }})</span>
                </div>
                <div class="import-feedback-preview__table-title-spacer" />
            </template>
            <template
                v-if="showHasHeaderRow"
                #headerActions>
                <pendo-checkbox v-model="hasHeaderRowValue" />
                First row contains headers
            </template>
        </pendo-table>
    </pendo-card>
</template>

<script>
import { PendoCard, PendoCheckbox, PendoTable, PendoTooltip } from '@pendo/components';
import { computed, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';

export default {
    name: 'ImportFeedbackPreviewTable',
    components: {
        PendoCard,
        PendoCheckbox,
        PendoTable,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        hasHeaderRow: {
            type: Boolean,
            default: true,
        },
        showHasHeaderRow: {
            type: Boolean,
            default: false,
        },
        tableHeaders: {
            type: Array,
            default: () => [],
        },
        tableData: {
            type: Array,
            default: () => [],
        },
        totalRows: {
            type: Number,
            default: 0,
        },
        filename: {
            type: String,
            default: '',
        },
    },
    emits: ['table-headers-changed'],
    setup (props, { emit }) {
        const refTableTitle = ref(null);
        const tableTitleTooltip = ref(null);
        const tableTitle = computed(() => `Sample rows from '${props.filename}'`);

        useResizeObserver(refTableTitle, (entries) => {
            const entry = entries[0];
            const { width } = entry.contentRect;
            const { scrollWidth } = entry.target;

            if (scrollWidth <= width) {
                tableTitleTooltip.value = null;
            } else {
                tableTitleTooltip.value = tableTitle.value;
            }
        });

        const hasHeaderRowValue = computed({
            get () {
                return props.hasHeaderRow;
            },
            set (hasHeaderRow) {
                emit('table-headers-changed', hasHeaderRow);
            },
        });

        const rows = computed(() => props.tableData.length);

        return {
            refTableTitle,
            tableTitle,
            tableTitleTooltip,
            hasHeaderRowValue,
            rows,
        };
    },
};
</script>

<style lang="less" scoped>
.import-feedback-preview {
    width: 70%;
    margin: 48px auto 0 auto;
    padding: 12px 20px 20px 20px;

    h3 {
        margin: 0 0 24px 0;
        font-size: 22px;
        font-weight: 600;
    }

    :deep(.pendo-table__title) {
        gap: 8px;
    }

    :deep(.pendo-table__active-count) {
        flex-shrink: 0;
        flex-grow: 0;
        line-height: 1.3;
    }

    &__table-title {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.3;
    }

    &__table-title-spacer {
        flex-grow: 1;
    }

    :deep(.pendo-table__title-actions) {
        font-size: 14px;
        display: flex;
        gap: 8px;
        align-items: center;
        flex-shrink: 0;
        flex-grow: 0;
        margin-right: 8px;
    }
}
</style>
